import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtom } from 'jotai';

import {
  Box,
  CircularProgress,
  DialogContent,
} from '@mui/material';

import {
  Typography
} from '../../../components/v2/styled';
import { Base } from './Base';

import { fetch_one } from '../../../lib/v31lib';
import { loginAtom } from '../../../lib/auth';

import { User } from '../../../models/User';

declare global {
  interface Window {
    ChippaScribe?: {
      mount: (el: HTMLElement, opts: { token: string; lang: string; accent?: string; context?: { title?: string; subtitle?: string } }) => void;
      unmount?: (el: HTMLElement) => void;
      open?: (opts: { token: string; lang: string; accent?: string; context?: { title?: string; subtitle?: string } }) => void;
      on?: (event: string, cb: () => void) => void;
    };
  }
}

type Props = {
  open: boolean;
  setOpen: (b: boolean) => void;
  advice: {
    id?: number;
    advice_date?: string;
    identification?: string;
  };
  user?: User;
  onImported?: () => void;
}

const POLL_INTERVAL = 10000;

export const ChippaScribeDialog: FunctionComponent<Props> = ({open, setOpen, advice, user, onImported}) => {
  const [login] = useAtom(loginAtom);
  const {t, i18n} = useTranslation(['translations']);
  const queryClient = useQueryClient();
  const mountRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>();

  const loadWidgetScript = () => {
    return new Promise<void>((resolve, reject) => {
      if (!!window.ChippaScribe) {
        resolve();
        return;
      }
      const existing = document.querySelector('script[src*="chippa"]');
      if (!!existing) {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', () => reject(new Error('script_error')));
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://app.chippa.io/widget.js';
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('script_error'));
      document.body.appendChild(script);
    });
  };

  const importMutation = useMutation({
    mutationFn: (pars: { advice_id: number }) => {
      return fetch_one<{success: boolean; status?: string}>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/chippa/import`,
        pars,
        login
      );
    },
    onSuccess: (data) => {
      if (!!data?.success) {
        queryClient.invalidateQueries({queryKey: ['advice', advice.id]});
        queryClient.invalidateQueries({queryKey: ['advices']});
        onImported?.();
      }
    },
    onError: () => {
      setError('import_error');
    }
  });

  useEffect(() => {
    if (!open || !advice?.id || !login?.id) return;

    let cancelled = false;
    setLoading(true);
    setError(undefined);

    (async () => {
      try {
        await loadWidgetScript();
        const session = await fetch_one<{token: string; options?: {accent?: string}}>(
          `/${i18n.resolvedLanguage || 'nl'}/v3/chippa/session`,
          {advice_id: advice.id},
          login
        );
        if (cancelled || !mountRef.current) return;
        mountRef.current.innerHTML = '';
        const chippaApi = window.ChippaScribe;
        chippaApi?.mount(mountRef.current, {
          token: session.token,
          lang: i18n.resolvedLanguage || 'nl',
          accent: session.options?.accent,
          context: {
            title: user?.full_name || '',
            subtitle: `${advice.identification || ''} ${advice.advice_date || ''}`.trim()
          }
        });
        if (!!chippaApi?.on) {
          chippaApi.on('report.approved', () => {
            const adviceId = advice.id;
            if (!!adviceId) importMutation.mutate({advice_id: adviceId});
          });
        }
        setLoading(false);
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.message || 'error');
        setLoading(false);
      }
    })();

    // poll: als het approved-event niet afkomt, import proberen zolang de popup open is
    const pollTimer = setInterval(() => {
      const adviceId = advice.id;
      if (!!adviceId && !importMutation.isPending && !importMutation.isSuccess) {
        importMutation.mutate({advice_id: adviceId});
      }
    }, POLL_INTERVAL);

    return () => {
      cancelled = true;
      clearInterval(pollTimer);
    };
  }, [open, advice?.id]);

  return <Base
    name="chippa_scribe"
    title={t("coach.chippa.title", "Chippa Scribe")}
    fullWidth
    maxWidth="lg"
    content={<DialogContent>
      {loading && <Box sx={{textAlign: 'center', py: 4}}>
        <CircularProgress />
        <Typography sx={{marginTop: 1}}>{t("coach.chippa.loading")}</Typography>
      </Box>}
      {!!error && <Typography sx={{color: 'error.main'}}>
        {t(`coach.chippa.error.${error === 'not_configured' ? 'not_configured' : 'generic'}`)}
      </Typography>}
      <Box ref={mountRef} sx={{minHeight: 300, display: loading ? 'none' : 'block'}} />
      {importMutation.isPending && !loading && <Typography sx={{marginTop: 1}}>{t("coach.chippa.checking")}</Typography>}
      {importMutation.isSuccess && !importMutation.isPending && (importMutation.data?.success
        ? <Typography sx={{marginTop: 1}}>{t("coach.chippa.imported")}</Typography>
        : <Typography sx={{marginTop: 1, color: 'warning.main'}}>{t("coach.chippa.waiting_approval")}</Typography>)}
    </DialogContent>}
    actions={<Box />}
    open={open}
    setOpen={setOpen}
    onClose={() => setOpen(false)}
  />;
};
